import React from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Home, ShoppingBag, ShoppingCart, User, MessageCircle, FileText, Phone, Mail, MapPin, Instagram, PackageSearch } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

function LangToggle() {
  const { lang, toggle } = useLang();
  return (
    <button onClick={toggle} data-testid="lang-toggle"
      className="text-xs tracking-[0.18em] uppercase text-text-muted hover:text-accent transition-colors">
      {lang === "ar" ? "EN" : "ع"}
    </button>
  );
}

export default function StoreLayout() {
  const { t, lang } = useLang();
  const { count } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const links = [
    { to: "/", label: t("home") },
    { to: "/shop", label: t("shop") },
    { to: "/policies", label: lang === "ar" ? "السياسات" : "Policies" },
    { to: "/contact", label: lang === "ar" ? "تواصل" : "Contact" },
  ];
  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));
  const hideFab = /^\/(product|cart|checkout|pay)/.test(loc.pathname);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#F9F6F0]/85 backdrop-blur-md border-b border-[#B89C72]/20">
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 h-[76px] md:h-[96px] flex items-center justify-between gap-6">
          <Link to="/" data-testid="brand-logo" className="flex items-center shrink-0">
            <img src="/logo.png" alt="RNF_Line" className="h-[68px] md:h-[88px] w-auto object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-9 absolute left-1/2 -translate-x-1/2">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to}`}
                className={`text-sm tracking-wide transition-colors ${isActive(l.to) ? "text-accent" : "text-text-muted hover:text-accent"}`}>
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-4 md:gap-5 shrink-0">
            <LangToggle />
            <Link to="/cart" data-testid="header-cart" className="relative p-1.5 hidden md:block">
              <ShoppingCart className="w-6 h-6" strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute -top-0.5 -end-0.5 bg-[#141110] text-[#F9F6F0] text-[11px] w-5 h-5 flex items-center justify-center rounded-full">{count}</span>
              )}
            </Link>
            <button data-testid="header-account" onClick={() => nav(user ? "/account" : "/login")} className="p-1.5 hidden md:block">
              <User className="w-6 h-6" strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#141110] text-[#EFE7D8] mt-0">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-14 md:py-16 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          <div>
            <img src="/logo.png" alt="RNF_Line" className="h-16 md:h-20 w-auto object-contain mb-4" style={{ filter: "brightness(0) invert(1)" }} />
            <p className="font-display text-lg text-[#EFE7D8]/90 mb-1">{lang === "ar" ? "أناقة تليق بكِ، وتفاصيل تشبهكِ" : "Elegance that suits you"}</p>
            <p className="text-[#EFE7D8]/50 text-sm leading-relaxed">{lang === "ar" ? "عبايات رنف — تُفصّل خصيصاً حسب مقاساتك في الكويت." : "RNF abayas — tailored to your measurements in Kuwait."}</p>
          </div>
          <div className="space-y-3">
            <div className="text-[#B89C72] text-xs tracking-[0.18em] uppercase mb-1">{lang === "ar" ? "تواصلي معنا" : "Contact us"}</div>
            <a href={`https://wa.me/96566382446`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2 text-[#EFE7D8]/80 hover:text-[#B89C72] transition-colors">
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> <span dir="ltr">+965 6638 2446</span>
            </a>
            <a href="mailto:ranfkw@gmail.com" data-testid="footer-email" className="flex items-center gap-2 text-[#EFE7D8]/80 hover:text-[#B89C72] transition-colors" dir="ltr">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> ranfkw@gmail.com
            </a>
            <a href="https://instagram.com/rnf_line" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="flex items-center gap-2 text-[#EFE7D8]/80 hover:text-[#B89C72] transition-colors" dir="ltr">
              <Instagram className="w-4 h-4" strokeWidth={1.5} /> @rnf_line
            </a>
            <div className="flex items-start gap-2 text-[#EFE7D8]/60"><MapPin className="w-4 h-4 mt-0.5" strokeWidth={1.5} /> {lang === "ar" ? "الشويخ، مجمع A5، بوابة 3، محل رقم 321، الكويت" : "Shuwaikh, A5 Complex, Gate 3, Shop 321, Kuwait"}</div>
          </div>
          <div className="space-y-3">
            <div className="text-[#B89C72] text-xs tracking-[0.18em] uppercase mb-1">{lang === "ar" ? "روابط" : "Links"}</div>
            <Link to="/shop" className="block text-[#EFE7D8]/80 hover:text-[#B89C72] transition-colors">{lang === "ar" ? "المجموعة" : "The Collection"}</Link>
            <Link to="/track" className="block text-[#EFE7D8]/80 hover:text-[#B89C72] transition-colors">{lang === "ar" ? "تتبع الطلب" : "Track order"}</Link>
            <Link to="/policies" data-testid="footer-policies" className="block text-[#EFE7D8]/80 hover:text-[#B89C72] transition-colors">{lang === "ar" ? "السياسات" : "Policies"}</Link>
            <Link to="/contact" data-testid="footer-contact" className="block text-[#EFE7D8]/80 hover:text-[#B89C72] transition-colors">{lang === "ar" ? "تواصل معنا" : "Contact us"}</Link>
          </div>
        </div>
        <div className="border-t border-[#EFE7D8]/10 pt-5 pb-[96px] md:pb-5 text-center text-[#EFE7D8]/40 text-xs">© {new Date().getFullYear()} RNF_Line · رنف لاين</div>
      </footer>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#F9F6F0]/95 backdrop-blur-md border-t border-[#B89C72]/25">
        <div className="grid grid-cols-5">
          {[
            { to: "/", label: t("home"), icon: Home },
            { to: "/shop", label: t("shop"), icon: ShoppingBag },
            { to: "/track", label: lang === "ar" ? "تتبع الطلب" : "Track", icon: PackageSearch },
            { to: "/cart", label: t("cart"), icon: ShoppingCart, badge: true },
            { to: user ? "/account" : "/login", label: t("account"), icon: User },
          ].map((l) => {
            const Icon = l.icon;
            const active = isActive(l.to);
            return (
              <Link key={l.to} to={l.to} data-testid={`bottomnav-${l.to}`}
                className={`relative flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors ${active ? "text-accent" : "text-text-muted"}`}>
                <Icon className="w-5 h-5" strokeWidth={1.4} />
                {l.badge && count > 0 && (
                  <span className="absolute top-1 end-5 bg-[#141110] text-[#F9F6F0] text-[9px] min-w-4 h-4 px-1 flex items-center justify-center rounded-full">{count}</span>
                )}
                {l.label}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Floating WhatsApp — hidden inside product/cart/checkout/pay forms to avoid covering fields & buttons */}
      {!hideFab && (
        <a href={`https://wa.me/96566382446?text=${encodeURIComponent("مرحباً رنف لاين، أودّ الاستفسار عن التفصيل حسب مقاسي 🌸")}`} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
          style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0px) + 12px)" }}
          className="fixed z-50 md:!bottom-6 start-4 bg-[#141110] text-[#F9F6F0] w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:bg-[#3C0F1A] transition-colors">
          <MessageCircle className="w-5 h-5" strokeWidth={1.6} />
        </a>
      )}
    </div>
  );
}
